/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState, forwardRef, useRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy, useRowSelect } from "react-table";
import Checkbox from "@mui/material/Checkbox"; // Import Checkbox

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableBodyCell from "../Components/DataTableBodyCell";
import DataTableHeadCell from "../Components/DataTableHeadCell";

// import DataTable from "components/ui/Tables/DataTable";
// import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
// import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { logger } from "Shared/Utils/Logger";
import LoadingIndicator from "Shared/Components/LoadingIndicator";

interface Props {
  entriesPerPage?:
  | false
  | {
    defaultValue: number;
    entries: number[];
  };
  canSearch?: boolean;
  showTotalEntries?: boolean;
  table: {
    columns: { [key: string]: any }[];
    rows: { [key: string]: any }[];
  };
  pagination?: {
    variant: "contained" | "gradient";
    color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark" | "light";
  };
  isSorted?: boolean;
  noEndBorder?: boolean;
  goPage?: any;
  // 페이지 사이트 표시
  storePageSize?: number;
  setStorePageSize?: any;
  criteria?: any;
  onRowClick?: (row: any) => void; // 행 클릭 이벤트
  onSearch?: (value: string) => void; // 검색 이벤트
  enableSelection?: boolean; // 체크박스 선택 활성화 여부
  onSelectionChange?: (selectedRows: any[]) => void; // 선택된 행 변경 콜백
  additionalControls?: React.ReactNode; // 추가 컨트롤 (예: 필터)
}

// Indeterminate Checkbox Component for Header
const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }: any, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Checkbox
        {...rest}
        ref={resolvedRef}
        indeterminate={indeterminate}
        color="primary"
      />
    </div>
  );
});

function BasicTable({
  entriesPerPage = { defaultValue: 10, entries: [5, 10, 15, 20, 25, 30, 40, 50] },
  canSearch = false,
  showTotalEntries = true,
  table,
  pagination = { variant: "gradient", color: "info" },
  isSorted = true,
  noEndBorder = false,
  criteria,
  goPage = () => { },
  storePageSize,
  setStorePageSize = () => { },
  onRowClick,
  onSearch,
  enableSelection = false,
  onSelectionChange,
  isRowSelectable, // Add isRowSelectable prop
  additionalControls,
}: any) {
  // ... (omitted)

  let defaultValue: any;
  let entries: any[];

  if (entriesPerPage) {
    if (typeof entriesPerPage === "object" && entriesPerPage !== null) {
      defaultValue = entriesPerPage.defaultValue !== undefined ? entriesPerPage.defaultValue : 10;
      entries = entriesPerPage.entries ? entriesPerPage.entries : [5, 10, 15, 20, 25, 30, 40, 50];
    } else {
      // entriesPerPage가 boolean인 경우 기본값 사용
      defaultValue = 10;
      entries = [5, 10, 15, 20, 25, 30, 40, 50];
    }
  }
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      autoResetSelectedRows: false, // Prevent selection reset on data/columns update
      autoResetPage: false,
      getRowId: (row: any, rowIndex: number) => {
        // row.id나 row.seqId가 있으면 사용, 없으면 rowIndex를 fallback으로 사용
        // (예: UserTermsAgreementDTO는 agreementSeqId를 사용하는 등 화면마다 식별자 필드가 다름)
        const id = row.id ?? row.seqId ?? row.agreementSeqId ?? row.userSeqId ?? row.policySeqId;
        return id !== undefined && id !== null ? String(id) : String(rowIndex);
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (enableSelection) {
        hooks.visibleColumns.push((columns) => [
          {
            id: "selection",
            Header: (headerProps: any) => {
              const { getToggleAllRowsSelectedProps, state, toggleAllRowsSelected } = headerProps;

              if (
                data.length === 0 ||
                (isRowSelectable && !data.some((row: any) => isRowSelectable(row)))
              ) {
                return null;
              }

              if (isRowSelectable) {
                const selectableRows = data.filter((row: any) => isRowSelectable(row));
                const selectedSelectableCount = selectableRows.reduce((count: number, row: any) => {
                  // Use the same ID logic as getRowId
                  const id = row.id || row.seqId;
                  return count + (state.selectedRowIds[id] ? 1 : 0);
                }, 0);

                const isAllSelectableSelected = selectableRows.length > 0 && selectedSelectableCount === selectableRows.length;
                const isIndeterminate = selectedSelectableCount > 0 && !isAllSelectableSelected;

                return (
                  <IndeterminateCheckbox
                    {...getToggleAllRowsSelectedProps()}
                    checked={isAllSelectableSelected}
                    indeterminate={isIndeterminate}
                    onChange={() => {
                      if (isAllSelectableSelected) {
                        toggleAllRowsSelected(false);
                      } else {
                        toggleAllRowsSelected(true);
                      }
                    }}
                  />
                );
              }

              return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />;
            },
            Cell: ({ row }: any) => {
              if (isRowSelectable && !isRowSelectable(row.original)) {
                return null;
              }
              return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />;
            },
            width: "50px", // Set fixed width for checkbox column
            align: "center",
          },
          ...columns,
        ]);
      }
    }
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    selectedFlatRows, // Get selected rows
    state: { pageIndex, pageSize, globalFilter, selectedRowIds },
    toggleRowSelected, // Destructure toggleRowSelected to use in useEffect
  } = tableInstance;

  // Use a ref to track the previous selection IDs to avoid infinite loops
  const prevSelectedIdsRef = useRef<string>("");

  // Effect to notify parent about selection changes
  useEffect(() => {
    if (enableSelection && onSelectionChange) {
      const currentSelectedIds = Object.keys(selectedRowIds).sort().join(",");

      if (currentSelectedIds !== prevSelectedIdsRef.current) {
        prevSelectedIdsRef.current = currentSelectedIds;
        onSelectionChange(selectedFlatRows.map((row: any) => row.original));
      }
    }
  }, [selectedRowIds, enableSelection, onSelectionChange, selectedFlatRows]);


  // Effect to sanitize selection based on isRowSelectable (e.g. when Select All is clicked)
  useEffect(() => {
    if (enableSelection && isRowSelectable) {
      const unselectableSelectedRows = selectedFlatRows.filter(
        (row: any) => !isRowSelectable(row.original)
      );

      if (unselectableSelectedRows.length > 0) {
        unselectableSelectedRows.forEach((row: any) => {
          toggleRowSelected(row.id, false);
        });
      }
    }
  }, [selectedRowIds, enableSelection, isRowSelectable, selectedFlatRows, toggleRowSelected]);

  let canPagePrevious = canPreviousPage;
  let canPageNext = canNextPage;
  let canPageTenPrevious = false;
  let canPageTenNext = true;

  const PageNumber = criteria ? criteria.PageNumber : pageIndex;

  if (criteria) {
    const pageIndex = PageNumber;
    const pageCount = criteria.PageCount.length;

    canPagePrevious = pageIndex !== 0;
    canPageNext = pageIndex + 1 < pageCount;
    canPageTenPrevious = criteria.PageTenNumber >= 10;
    canPageTenNext =
      criteria.PageTenNumber !== Math.floor(criteria.totalCount / storePageSize / 10) * 10;
  }
  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value: any) => setPageSize(value);

  // Render the paginations
  const renderPagination = criteria
    ? criteria.PageCount.map((option: any) => {
      // const tenCount = criteria.PageTenNumber;
      if (criteria.PageTenNumber <= option && option < criteria.PageTenNumber + 10) {
        return (
          <MDPagination
            item
            key={option}
            onClick={() => goPage(Number(option))}
            active={PageNumber === option}
          >
            {option + 1}
          </MDPagination>
        );
      }
    })
    : pageOptions.map((option: any) => {
      if (option > 10) {
        return (
          <MDPagination
            item
            key={option}
            onClick={() => gotoPage(Number(option))}
            active={pageIndex === option}
          >
            {option + 1}
          </MDPagination>
        );
      }
    });

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }: any) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }: any) => gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column: any) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  let entriesStart: number;
  let entriesEnd: number;

  if (criteria) {
    // 서버 사이드 페이징인 경우
    const currentPageIndex = criteria.PageNumber;
    entriesStart = currentPageIndex * storePageSize + 1;
    entriesEnd = Math.min((currentPageIndex + 1) * storePageSize, criteria.totalCount);
  } else {
    // 클라이언트 사이드 페이징인 경우
    entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;
    if (pageIndex === 0) {
      entriesEnd = pageSize;
    } else if (pageIndex === pageOptions.length - 1) {
      entriesEnd = rows.length;
    } else {
      entriesEnd = pageSize * (pageIndex + 1);
    }
  }

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={storePageSize?.toString() || defaultValue?.toString() || "10"}
                options={entries.map((e: any) => e.toString())}
                getOptionLabel={(option) => option.toString()}
                isOptionEqualToValue={(option, value) => option === value}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props as any;
                  return (
                    <li key={key} {...otherProps}>
                      {option}
                    </li>
                  );
                }}
                onChange={(event, newValue) => {
                  const pageSizeValue = typeof newValue === "string" ? parseInt(newValue, 10) : newValue;
                  if (pageSizeValue && !isNaN(pageSizeValue)) {
                    setStorePageSize(pageSizeValue);
                    setEntriesPerPage(pageSizeValue);
                  }
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {additionalControls && (
            <MDBox ml={2}>
              {additionalControls}
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Search..."
                value={search || ""}
                size="small"
                fullWidth
                onChange={({ currentTarget }: any) => {
                  setSearch(currentTarget.value);
                  onSearch && onSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup: any, headerGroupIndex: any) => {
            const headerGroupProps = headerGroup.getHeaderGroupProps();
            const { key: headerGroupKey, ...headerGroupPropsWithoutKey } = headerGroupProps;
            return (
              <TableRow key={headerGroupKey || headerGroupIndex} {...headerGroupPropsWithoutKey}>
                {headerGroup.headers.map((column: any, columnIndex: any) => {
                  const columnProps = column.getHeaderProps(isSorted && column.getSortByToggleProps());
                  const { key: columnKey, ...columnPropsWithoutKey } = columnProps;
                  return (
                    <DataTableHeadCell
                      key={columnKey || columnIndex}
                      {...columnPropsWithoutKey}
                      width={column.width ? column.width : "auto"}
                      align={column.align ? column.align : "left"}
                      sorted={setSortedValue(column)}
                    >
                      {column.render("Header")}
                    </DataTableHeadCell>
                  );
                })}
              </TableRow>
            );
          })}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row: any, rowIndex: any) => {
            prepareRow(row);
            const rowProps = row.getRowProps();
            const { key: rowKey, ...rowPropsWithoutKey } = rowProps;
            return (
              <TableRow
                key={rowKey || rowIndex}
                {...rowPropsWithoutKey}
                onClick={() => onRowClick && onRowClick(row.original)}
                sx={{
                  cursor: onRowClick ? "pointer" : "default",
                  "&:hover": onRowClick ? { backgroundColor: "rgba(0, 0, 0, 0.04)" } : {},
                }}
              >
                {row.cells.map((cell: any, cellIndex: any) => {
                  const cellProps = cell.getCellProps();
                  const { key: cellKey, ...cellPropsWithoutKey } = cellProps;
                  return (
                    <DataTableBodyCell
                      key={cellKey || cellIndex}
                      noBorder={noEndBorder && rows.length - 1 === rowIndex}
                      align={cell.column.align ? cell.column.align : "left"}
                      {...cellPropsWithoutKey}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={
          !showTotalEntries && (criteria ? criteria.PageCount.length : pageOptions.length) === 1
            ? 0
            : 3
        }
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }} minWidth="250px">
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {criteria ? criteria.totalCount : rows.length} entries
            </MDTypography>
          </MDBox>
        )}
        {(criteria ? criteria.PageCount.length : pageOptions.length) > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            <MDPagination
              hide={false}
              item
              disabled={!canPageTenPrevious}
              onClick={() => {
                if (canPageTenPrevious) {
                  goPage(criteria.PageTenNumber - 10);
                }
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>keyboard_double_arrow_left</Icon>
            </MDPagination>
            <MDPagination
              hide={false}
              item
              disabled={!canPagePrevious}
              onClick={() => {
                if (canPagePrevious) {
                  goPage(PageNumber - 1);
                }
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
            </MDPagination>

            {renderPagination}

            <MDPagination
              hide={false}
              item
              disabled={!canPageNext}
              onClick={() => {
                if (canPageNext) {
                  goPage(PageNumber + 1);
                }
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDPagination>
            <MDPagination
              hide={false}
              item
              disabled={!canPageTenNext}
              onClick={() => {
                if (canPageTenNext) {
                  goPage(criteria.PageTenNumber + 10);
                }
              }}
            >
              <Icon sx={{ fontWeight: "bold" }}>keyboard_double_arrow_right</Icon>
            </MDPagination>
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Default props are now handled using JavaScript default parameters in the function signature

// Typechecking props for the DataTable
BasicTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  goPage: PropTypes.func,
  setStorePageSize: PropTypes.func,
  storePageSize: PropTypes.any,
  criteria: PropTypes.any,
  onRowClick: PropTypes.func,
  onSearch: PropTypes.func,
  enableSelection: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  isRowSelectable: PropTypes.func,
  additionalControls: PropTypes.node,
};

export default BasicTable;
